import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Star, Lock, Check, RotateCcw, Sparkles, Volume2, VolumeX } from 'lucide-react';
import axios from 'axios';
import { API, useAuth } from '../App';
import BlockyCharacter3D from '../components/BlockyCharacter3D';
import { useTTS, PLAYER_VOICES } from '../hooks/useTTS';

// Customization Options
const CUSTOMIZATION_OPTIONS = {
  voice: [
    { id: 'alice', name: 'Alice', cost: 0, description: 'Clear and friendly', gender: 'female' },
    { id: 'george', name: 'George', cost: 15, description: 'Warm storyteller', gender: 'male' },
    { id: 'lily', name: 'Lily', cost: 15, description: 'Confident and bright', gender: 'female' },
    { id: 'daniel', name: 'Daniel', cost: 20, description: 'Steady and clear', gender: 'male' },
  ],
  skinTone: [
    { id: '#FFE0BD', name: 'Light', cost: 0 },
    { id: '#F5D0B0', name: 'Fair', cost: 0 },
    { id: '#D4A574', name: 'Medium', cost: 0 },
    { id: '#C68642', name: 'Tan', cost: 0 },
    { id: '#8D5524', name: 'Brown', cost: 0 },
    { id: '#5C3A21', name: 'Dark', cost: 0 },
    { id: '#3D2314', name: 'Deep', cost: 0 },
  ],
  hairColor: [
    { id: '#1a1a1a', name: 'Black', cost: 0 },
    { id: '#4a3728', name: 'Brown', cost: 0 },
    { id: '#6B4423', name: 'Chestnut', cost: 0 },
    { id: '#E8D5A3', name: 'Blonde', cost: 0 },
    { id: '#F5E6C8', name: 'Platinum', cost: 15 },
    { id: '#A52A2A', name: 'Auburn', cost: 0 },
    { id: '#B87333', name: 'Copper', cost: 10 },
    { id: '#808080', name: 'Gray', cost: 0 },
    { id: '#FF6B35', name: 'Orange', cost: 20 },
    { id: '#4169E1', name: 'Blue', cost: 30 },
    { id: '#FF69B4', name: 'Pink', cost: 30 },
    { id: '#8B5CF6', name: 'Purple', cost: 30 },
    { id: '#22C55E', name: 'Green', cost: 30 },
    { id: '#EF4444', name: 'Red', cost: 25 },
    { id: '#00CED1', name: 'Teal', cost: 35 },
    { id: '#FFD700', name: 'Gold', cost: 40, stage: 3 },
  ],
  hairStyle: [
    { id: 'short', name: 'Short', cost: 0 },
    { id: 'long', name: 'Long', cost: 0 },
    { id: 'curly', name: 'Curly', cost: 0 },
    { id: 'bob', name: 'Bob', cost: 0 },
    { id: 'buzz', name: 'Buzz Cut', cost: 0 },
    { id: 'bald', name: 'Bald', cost: 0 },
    { id: 'spiky', name: 'Spiky', cost: 20 },
    { id: 'ponytail', name: 'Ponytail', cost: 15 },
    { id: 'pigtails', name: 'Pigtails', cost: 15 },
    { id: 'braids', name: 'Braids', cost: 25 },
    { id: 'afro', name: 'Afro', cost: 30 },
    { id: 'mohawk', name: 'Mohawk', cost: 40, stage: 2 },
    { id: 'bun', name: 'Bun', cost: 20 },
    { id: 'wavy', name: 'Wavy', cost: 15 },
    { id: 'undercut', name: 'Undercut', cost: 25 },
    { id: 'dreadlocks', name: 'Dreadlocks', cost: 35 },
    { id: 'sidepart', name: 'Side Part', cost: 10 },
    { id: 'mullet', name: 'Mullet', cost: 30, stage: 2 },
  ],
  shirtColor: [
    { id: '#4A90D9', name: 'Blue', cost: 0 },
    { id: '#22C55E', name: 'Green', cost: 0 },
    { id: '#EF4444', name: 'Red', cost: 0 },
    { id: '#F59E0B', name: 'Orange', cost: 0 },
    { id: '#8B5CF6', name: 'Purple', cost: 0 },
    { id: '#EC4899', name: 'Pink', cost: 0 },
    { id: '#FFFFFF', name: 'White', cost: 0 },
    { id: '#1F2937', name: 'Black', cost: 0 },
    { id: '#FFD700', name: 'Gold', cost: 50, stage: 3 },
    { id: '#00BFFF', name: 'Cyan', cost: 30 },
    { id: '#9CA3AF', name: 'Gray', cost: 0 },
    { id: '#7C3AED', name: 'Violet', cost: 20 },
    { id: '#14B8A6', name: 'Teal', cost: 25 },
    { id: '#F97316', name: 'Tangerine', cost: 15 },
  ],
  pantsColor: [
    { id: '#3B82F6', name: 'Blue Jeans', cost: 0 },
    { id: '#1F2937', name: 'Black', cost: 0 },
    { id: '#78716C', name: 'Gray', cost: 0 },
    { id: '#84CC16', name: 'Green', cost: 15 },
    { id: '#7C3AED', name: 'Purple', cost: 20 },
    { id: '#8B4513', name: 'Brown', cost: 0 },
    { id: '#F5F5DC', name: 'Beige', cost: 10 },
    { id: '#DC143C', name: 'Red', cost: 25 },
    { id: '#4169E1', name: 'Royal Blue', cost: 20 },
    { id: '#FF69B4', name: 'Pink', cost: 30, stage: 2 },
  ],
  shoeStyle: [
    { id: 'trainers', name: 'Trainers', cost: 0 },
    { id: 'boots', name: 'Boots', cost: 20 },
    { id: 'sandals', name: 'Sandals', cost: 15 },
    { id: 'barefoot', name: 'Bare Feet', cost: 0 },
  ],
  eyeStyle: [
    { id: 'normal', name: 'Normal', cost: 0 },
    { id: 'happy', name: 'Happy', cost: 0 },
    { id: 'cool', name: 'Cool', cost: 25 },
    { id: 'wink', name: 'Wink', cost: 20 },
    { id: 'surprised', name: 'Surprised', cost: 15 },
    { id: 'sleepy', name: 'Sleepy', cost: 20 },
    { id: 'excited', name: 'Excited', cost: 25 },
    { id: 'angry', name: 'Angry', cost: 30, stage: 2 },
  ],
  mouthStyle: [
    { id: 'smile', name: 'Smile', cost: 0 },
    { id: 'grin', name: 'Grin', cost: 0 },
    { id: 'neutral', name: 'Neutral', cost: 0 },
    { id: 'open', name: 'Open', cost: 10 },
    { id: 'tongue', name: 'Tongue Out', cost: 20 },
    { id: 'surprised', name: 'Surprised', cost: 15 },
    { id: 'teeth', name: 'Teeth', cost: 25 },
    { id: 'kiss', name: 'Kiss', cost: 30, stage: 2 },
  ],
  accessory: [
    { id: 'none', name: 'None', cost: 0 },
    { id: 'glasses', name: 'Glasses', cost: 25 },
    { id: 'sunglasses', name: 'Sunglasses', cost: 30 },
    { id: 'cap', name: 'Cap', cost: 20 },
    { id: 'headband', name: 'Headband', cost: 15 },
    { id: 'hat', name: 'Cowboy Hat', cost: 30 },
    { id: 'bow', name: 'Bow', cost: 10 },
    { id: 'headphones', name: 'Headphones', cost: 40 },
    { id: 'backpack', name: 'Backpack', cost: 35 },
    { id: 'guitar', name: 'Guitar', cost: 50, stage: 2 },
    { id: 'crown', name: 'Crown', cost: 100, stage: 4 },
    { id: 'earrings', name: 'Earrings', cost: 20 },
    { id: 'necklace', name: 'Necklace', cost: 25 },
    { id: 'scarf', name: 'Scarf', cost: 30 },
    { id: 'watch', name: 'Watch', cost: 35 },
    { id: 'wings', name: 'Wings', cost: 60, stage: 3 },
    { id: 'halo', name: 'Halo', cost: 50, stage: 3 },
    { id: 'wizardhat', name: 'Wizard Hat', cost: 45, stage: 2 },
    { id: 'piratehat', name: 'Pirate Hat', cost: 40, stage: 2 },
    { id: 'bunnyears', name: 'Bunny Ears', cost: 35 },
    { id: 'catears', name: 'Cat Ears', cost: 35 },
    { id: 'santahat', name: 'Santa Hat', cost: 40 },
  ],
};

// Color preview component
const ColorPreview = ({ color }) => (
  <div 
    className="w-10 h-10 rounded-lg border-2 border-white/20 shadow-md"
    style={{ backgroundColor: color }}
  />
);

// Hair style preview
const HairPreview = ({ style, color }) => (
  <div className="w-10 h-10 flex items-center justify-center">
    {style === 'short' && (
      <div style={{ width: 32, height: 12, backgroundColor: color, borderRadius: '4px 4px 0 0' }} />
    )}
    {style === 'long' && (
      <div className="relative" style={{ width: 36, height: 28 }}>
        <div style={{ position: 'absolute', top: 0, width: 36, height: 14, backgroundColor: color, borderRadius: '6px 6px 0 0' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 2, width: 8, height: 16, backgroundColor: color, borderRadius: 2 }} />
        <div style={{ position: 'absolute', bottom: 0, right: 2, width: 8, height: 16, backgroundColor: color, borderRadius: 2 }} />
      </div>
    )}
    {style === 'curly' && (
      <div style={{ width: 34, height: 20, backgroundColor: color, borderRadius: '50%' }} />
    )}
    {style === 'bob' && (
      <div className="relative" style={{ width: 36, height: 26 }}>
        <div style={{ position: 'absolute', top: 0, width: 36, height: 14, backgroundColor: color, borderRadius: '8px 8px 0 0' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: 10, height: 18, backgroundColor: color, borderRadius: '0 0 6px 6px' }} />
        <div style={{ position: 'absolute', bottom: 0, right: 0, width: 10, height: 18, backgroundColor: color, borderRadius: '0 0 6px 6px' }} />
      </div>
    )}
    {style === 'buzz' && (
      <div style={{ width: 32, height: 8, backgroundColor: color, borderRadius: '3px 3px 0 0', opacity: 0.8 }} />
    )}
    {style === 'spiky' && (
      <div className="flex gap-1 items-end">
        <div style={{ width: 8, height: 16, backgroundColor: color, clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }} />
        <div style={{ width: 10, height: 22, backgroundColor: color, clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }} />
        <div style={{ width: 8, height: 16, backgroundColor: color, clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }} />
      </div>
    )}
    {style === 'ponytail' && (
      <div className="relative" style={{ width: 34, height: 32 }}>
        <div style={{ position: 'absolute', top: 0, width: 34, height: 12, backgroundColor: color, borderRadius: '4px 4px 0 0' }} />
        <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: 10, height: 22, backgroundColor: color, borderRadius: 4 }} />
      </div>
    )}
    {style === 'pigtails' && (
      <div className="relative" style={{ width: 38, height: 28 }}>
        <div style={{ position: 'absolute', top: 0, left: 5, width: 28, height: 10, backgroundColor: color, borderRadius: '4px 4px 0 0' }} />
        <div style={{ position: 'absolute', top: 6, left: 0, width: 8, height: 22, backgroundColor: color, borderRadius: '0 0 4px 4px' }} />
        <div style={{ position: 'absolute', top: 6, right: 0, width: 8, height: 22, backgroundColor: color, borderRadius: '0 0 4px 4px' }} />
        <div style={{ position: 'absolute', top: 8, left: 1, width: 6, height: 3, backgroundColor: '#EC4899', borderRadius: 1 }} />
        <div style={{ position: 'absolute', top: 8, right: 1, width: 6, height: 3, backgroundColor: '#EC4899', borderRadius: 1 }} />
      </div>
    )}
    {style === 'braids' && (
      <div className="relative" style={{ width: 34, height: 32 }}>
        <div style={{ position: 'absolute', top: 0, left: 3, width: 28, height: 10, backgroundColor: color, borderRadius: '4px 4px 0 0' }} />
        <div style={{ position: 'absolute', top: 8, left: 2, width: 6, height: 24, backgroundColor: color, borderRadius: '0 0 3px 3px' }}>
          {[0, 6, 12, 18].map((y, i) => <div key={i} style={{ position: 'absolute', top: y, width: 6, height: 4, backgroundColor: i%2===0 ? color : 'rgba(0,0,0,0.15)', borderRadius: 1 }} />)}
        </div>
        <div style={{ position: 'absolute', top: 8, right: 2, width: 6, height: 24, backgroundColor: color, borderRadius: '0 0 3px 3px' }}>
          {[0, 6, 12, 18].map((y, i) => <div key={i} style={{ position: 'absolute', top: y, width: 6, height: 4, backgroundColor: i%2===0 ? color : 'rgba(0,0,0,0.15)', borderRadius: 1 }} />)}
        </div>
      </div>
    )}
    {style === 'afro' && (
      <div className="relative" style={{ width: 38, height: 30 }}>
        <div style={{ width: 38, height: 30, backgroundColor: color, borderRadius: '50%' }} />
        {[0, 10, 20, 30].map((x, i) => <div key={i} style={{ position: 'absolute', top: -2, left: 2 + x, width: 8, height: 8, backgroundColor: color, borderRadius: '50%', opacity: 0.8 }} />)}
      </div>
    )}
    {style === 'mohawk' && (
      <div style={{ width: 10, height: 28, backgroundColor: color, borderRadius: '3px 3px 0 0' }} />
    )}
    {style === 'bald' && (
      <div className="w-8 h-8 rounded-full" style={{ backgroundColor: '#FFE0BD', border: '2px solid #ddd' }} />
    )}
    {style === 'bun' && (
      <div className="relative" style={{ width: 34, height: 28 }}>
        <div style={{ position: 'absolute', top: 8, width: 34, height: 12, backgroundColor: color, borderRadius: '4px 4px 0 0' }} />
        <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 16, height: 16, backgroundColor: color, borderRadius: '50%' }} />
      </div>
    )}
    {style === 'wavy' && (
      <div className="relative" style={{ width: 36, height: 26 }}>
        <div style={{ width: 36, height: 16, backgroundColor: color, borderRadius: '8px 8px 0 0' }} />
        <svg width="36" height="12" style={{ position: 'absolute', bottom: 0 }}>
          <path d="M0,6 Q9,0 18,6 T36,6" stroke={color} fill="none" strokeWidth="8" />
        </svg>
      </div>
    )}
    {style === 'undercut' && (
      <div className="relative" style={{ width: 32, height: 20 }}>
        <div style={{ width: 32, height: 14, backgroundColor: color, borderRadius: '4px 4px 0 0' }} />
        <div style={{ position: 'absolute', bottom: 0, width: 32, height: 6, backgroundColor: color, opacity: 0.4, borderRadius: '0 0 2px 2px' }} />
      </div>
    )}
    {style === 'dreadlocks' && (
      <div className="relative" style={{ width: 38, height: 32 }}>
        <div style={{ position: 'absolute', top: 0, left: 3, width: 32, height: 10, backgroundColor: color, borderRadius: '4px 4px 0 0' }} />
        {[0, 8, 16, 24].map((x, i) => (
          <div key={i} style={{ position: 'absolute', top: 8, left: 5 + x, width: 6, height: 24, backgroundColor: color, borderRadius: 3 }} />
        ))}
      </div>
    )}
    {style === 'sidepart' && (
      <div className="relative" style={{ width: 34, height: 18 }}>
        <div style={{ width: 34, height: 14, backgroundColor: color, borderRadius: '4px 4px 0 0' }} />
        <div style={{ position: 'absolute', top: 2, left: 8, width: 2, height: 10, backgroundColor: 'rgba(0,0,0,0.2)' }} />
      </div>
    )}
    {style === 'mullet' && (
      <div className="relative" style={{ width: 36, height: 32 }}>
        <div style={{ position: 'absolute', top: 0, width: 36, height: 12, backgroundColor: color, borderRadius: '4px 4px 0 0' }} />
        <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', width: 28, height: 22, backgroundColor: color, borderRadius: '0 0 4px 4px' }} />
      </div>
    )}
  </div>
);

// Accessory preview
const AccessoryPreview = ({ type }) => (
  <div className="w-10 h-10 flex items-center justify-center">
    {type === 'none' && <div className="text-gray-400">-</div>}
    {type === 'hat' && (
      <div className="relative" style={{ width: 36, height: 24 }}>
        <div style={{ position: 'absolute', bottom: 0, width: 36, height: 6, backgroundColor: '#8B4513', borderRadius: 2 }} />
        <div style={{ position: 'absolute', bottom: 6, left: 8, width: 20, height: 14, backgroundColor: '#8B4513', borderRadius: 3 }} />
      </div>
    )}
    {type === 'cap' && (
      <div className="relative" style={{ width: 34, height: 18 }}>
        <div style={{ position: 'absolute', bottom: 0, width: 34, height: 12, backgroundColor: '#E53935', borderRadius: '6px 6px 0 0' }} />
        <div style={{ position: 'absolute', bottom: 1, left: 0, width: 16, height: 5, backgroundColor: '#C62828', borderRadius: 2 }} />
      </div>
    )}
    {type === 'crown' && (
      <div style={{ width: 28, height: 18, backgroundColor: '#FFD700', clipPath: 'polygon(0% 100%, 15% 30%, 30% 70%, 50% 0%, 70% 70%, 85% 30%, 100% 100%)' }} />
    )}
    {type === 'headphones' && (
      <div className="relative" style={{ width: 36, height: 26 }}>
        <div style={{ position: 'absolute', top: 0, width: 36, height: 6, backgroundColor: '#333', borderRadius: 3 }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: 10, height: 16, backgroundColor: '#1F2937', borderRadius: 2 }} />
        <div style={{ position: 'absolute', bottom: 0, right: 0, width: 10, height: 16, backgroundColor: '#1F2937', borderRadius: 2 }} />
      </div>
    )}
    {type === 'glasses' && (
      <div className="relative" style={{ width: 36, height: 14 }}>
        <div style={{ position: 'absolute', left: 0, width: 14, height: 10, border: '2px solid #333', borderRadius: 2 }} />
        <div style={{ position: 'absolute', right: 0, width: 14, height: 10, border: '2px solid #333', borderRadius: 2 }} />
        <div style={{ position: 'absolute', left: 14, top: 3, width: 8, height: 2, backgroundColor: '#333' }} />
      </div>
    )}
    {type === 'sunglasses' && (
      <div className="relative" style={{ width: 36, height: 14 }}>
        <div style={{ position: 'absolute', left: 0, width: 14, height: 10, backgroundColor: 'rgba(20,20,20,0.75)', border: '2px solid #1a1a1a', borderRadius: 3 }} />
        <div style={{ position: 'absolute', right: 0, width: 14, height: 10, backgroundColor: 'rgba(20,20,20,0.75)', border: '2px solid #1a1a1a', borderRadius: 3 }} />
        <div style={{ position: 'absolute', left: 14, top: 3, width: 8, height: 2, backgroundColor: '#1a1a1a' }} />
      </div>
    )}
    {type === 'headband' && (
      <div style={{ width: 36, height: 6, backgroundColor: '#EF4444', borderRadius: 2 }} />
    )}
    {type === 'bow' && (
      <div className="relative" style={{ width: 24, height: 18 }}>
        <div style={{ position: 'absolute', left: 0, width: 10, height: 10, backgroundColor: '#EC4899', borderRadius: 3, transform: 'rotate(-15deg)' }} />
        <div style={{ position: 'absolute', right: 0, width: 10, height: 10, backgroundColor: '#EC4899', borderRadius: 3, transform: 'rotate(15deg)' }} />
        <div style={{ position: 'absolute', left: 9, top: 4, width: 6, height: 10, backgroundColor: '#BE185D', borderRadius: 2 }} />
      </div>
    )}
    {type === 'backpack' && (
      <div className="relative" style={{ width: 24, height: 28 }}>
        <div style={{ width: 24, height: 28, backgroundColor: '#7C3AED', borderRadius: 4 }} />
        <div style={{ position: 'absolute', bottom: 3, left: 6, width: 12, height: 8, backgroundColor: '#6D28D9', borderRadius: 2 }} />
        <div style={{ position: 'absolute', top: 2, left: 3, width: 4, height: 10, backgroundColor: '#8B5CF6', borderRadius: 1 }} />
        <div style={{ position: 'absolute', top: 2, right: 3, width: 4, height: 10, backgroundColor: '#8B5CF6', borderRadius: 1 }} />
      </div>
    )}
    {type === 'guitar' && (
      <div className="relative" style={{ width: 18, height: 32 }}>
        <div style={{ position: 'absolute', bottom: 0, left: 2, width: 14, height: 18, backgroundColor: '#D97706', borderRadius: '50% 50% 40% 40%' }}>
          <div style={{ position: 'absolute', top: 6, left: 5, width: 4, height: 4, backgroundColor: '#1a1a1a', borderRadius: '50%' }} />
        </div>
        <div style={{ position: 'absolute', top: 0, left: 7, width: 4, height: 16, backgroundColor: '#92400E', borderRadius: 1 }} />
      </div>
    )}
    {type === 'earrings' && (
      <div className="relative" style={{ width: 32, height: 20 }}>
        <div style={{ position: 'absolute', left: 2, top: 4, width: 8, height: 8, backgroundColor: '#FFD700', borderRadius: '50%' }} />
        <div style={{ position: 'absolute', right: 2, top: 4, width: 8, height: 8, backgroundColor: '#FFD700', borderRadius: '50%' }} />
      </div>
    )}
    {type === 'necklace' && (
      <div className="relative" style={{ width: 28, height: 20 }}>
        <div style={{ width: 28, height: 12, border: '3px solid #FFD700', borderTop: 'none', borderRadius: '0 0 14px 14px' }} />
        <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: 8, height: 8, backgroundColor: '#EF4444', borderRadius: '50%' }} />
      </div>
    )}
    {type === 'scarf' && (
      <div className="relative" style={{ width: 32, height: 24 }}>
        <div style={{ width: 32, height: 10, backgroundColor: '#EF4444', borderRadius: 4 }} />
        <div style={{ position: 'absolute', bottom: 0, right: 4, width: 10, height: 16, backgroundColor: '#EF4444', borderRadius: 3 }} />
      </div>
    )}
    {type === 'watch' && (
      <div className="relative" style={{ width: 16, height: 22 }}>
        <div style={{ position: 'absolute', top: 0, left: 4, width: 8, height: 6, backgroundColor: '#333', borderRadius: 2 }} />
        <div style={{ position: 'absolute', top: 5, left: 0, width: 16, height: 12, backgroundColor: '#1F2937', borderRadius: 3, border: '2px solid #6B7280' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 4, width: 8, height: 6, backgroundColor: '#333', borderRadius: 2 }} />
      </div>
    )}
    {type === 'wings' && (
      <div className="relative" style={{ width: 36, height: 28 }}>
        <div style={{ position: 'absolute', left: 0, width: 14, height: 24, backgroundColor: '#E0E7FF', borderRadius: '50% 10% 50% 50%', border: '1px solid #A5B4FC' }} />
        <div style={{ position: 'absolute', right: 0, width: 14, height: 24, backgroundColor: '#E0E7FF', borderRadius: '10% 50% 50% 50%', border: '1px solid #A5B4FC' }} />
      </div>
    )}
    {type === 'halo' && (
      <div style={{ width: 28, height: 12, border: '4px solid #FFD700', borderRadius: '50%', backgroundColor: 'rgba(255,215,0,0.2)' }} />
    )}
    {type === 'wizardhat' && (
      <div className="relative" style={{ width: 28, height: 32 }}>
        <div style={{ position: 'absolute', bottom: 0, width: 28, height: 6, backgroundColor: '#4F46E5', borderRadius: 2 }} />
        <div style={{ position: 'absolute', bottom: 4, left: 4, width: 20, height: 24, backgroundColor: '#6366F1', clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }} />
        <div style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', width: 6, height: 6, backgroundColor: '#FFD700', borderRadius: '50%' }} />
      </div>
    )}
    {type === 'piratehat' && (
      <div className="relative" style={{ width: 34, height: 22 }}>
        <div style={{ position: 'absolute', bottom: 0, width: 34, height: 8, backgroundColor: '#1F2937', borderRadius: 3 }} />
        <div style={{ position: 'absolute', bottom: 6, left: 4, width: 26, height: 14, backgroundColor: '#1F2937', borderRadius: '8px 8px 0 0' }} />
        <div style={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', width: 10, height: 10, backgroundColor: '#FFF', clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }} />
      </div>
    )}
    {type === 'bunnyears' && (
      <div className="relative" style={{ width: 28, height: 32 }}>
        <div style={{ position: 'absolute', left: 2, bottom: 0, width: 8, height: 28, backgroundColor: '#FFF', borderRadius: '8px 8px 4px 4px', border: '1px solid #ddd' }}>
          <div style={{ position: 'absolute', top: 4, left: 2, width: 4, height: 16, backgroundColor: '#FECACA', borderRadius: 2 }} />
        </div>
        <div style={{ position: 'absolute', right: 2, bottom: 0, width: 8, height: 28, backgroundColor: '#FFF', borderRadius: '8px 8px 4px 4px', border: '1px solid #ddd' }}>
          <div style={{ position: 'absolute', top: 4, left: 2, width: 4, height: 16, backgroundColor: '#FECACA', borderRadius: 2 }} />
        </div>
      </div>
    )}
    {type === 'catears' && (
      <div className="relative" style={{ width: 32, height: 20 }}>
        <div style={{ position: 'absolute', left: 0, bottom: 0, width: 12, height: 16, backgroundColor: '#1F2937', clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }}>
          <div style={{ position: 'absolute', top: 6, left: 3, width: 6, height: 8, backgroundColor: '#FECACA', clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }} />
        </div>
        <div style={{ position: 'absolute', right: 0, bottom: 0, width: 12, height: 16, backgroundColor: '#1F2937', clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }}>
          <div style={{ position: 'absolute', top: 6, left: 3, width: 6, height: 8, backgroundColor: '#FECACA', clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }} />
        </div>
      </div>
    )}
    {type === 'santahat' && (
      <div className="relative" style={{ width: 28, height: 28 }}>
        <div style={{ position: 'absolute', bottom: 0, width: 28, height: 8, backgroundColor: '#FFF', borderRadius: 3 }} />
        <div style={{ position: 'absolute', bottom: 6, left: 2, width: 24, height: 18, backgroundColor: '#DC2626', clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }} />
        <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 10, height: 10, backgroundColor: '#FFF', borderRadius: '50%' }} />
      </div>
    )}
  </div>
);

// Eye style preview
const EyePreview = ({ style }) => (
  <div className="w-10 h-10 flex items-center justify-center bg-cream rounded-lg">
    <svg width="32" height="20" viewBox="0 0 32 20">
      {style === 'normal' && (
        <>
          <rect x="2" y="4" width="10" height="10" rx="2" fill="white" stroke="#333" strokeWidth="1"/>
          <circle cx="7" cy="9" r="3" fill="#1a1a1a"/>
          <rect x="20" y="4" width="10" height="10" rx="2" fill="white" stroke="#333" strokeWidth="1"/>
          <circle cx="25" cy="9" r="3" fill="#1a1a1a"/>
        </>
      )}
      {style === 'happy' && (
        <>
          <path d="M 2 10 Q 7 4 12 10" stroke="#1a1a1a" strokeWidth="2" fill="none"/>
          <path d="M 20 10 Q 25 4 30 10" stroke="#1a1a1a" strokeWidth="2" fill="none"/>
        </>
      )}
      {style === 'cool' && (
        <>
          <rect x="0" y="6" width="14" height="8" rx="1" fill="#1a1a1a"/>
          <rect x="18" y="6" width="14" height="8" rx="1" fill="#1a1a1a"/>
          <line x1="14" y1="10" x2="18" y2="10" stroke="#1a1a1a" strokeWidth="2"/>
        </>
      )}
      {style === 'wink' && (
        <>
          <rect x="2" y="4" width="10" height="10" rx="2" fill="white" stroke="#333" strokeWidth="1"/>
          <circle cx="7" cy="9" r="3" fill="#1a1a1a"/>
          <path d="M 20 10 Q 25 4 30 10" stroke="#1a1a1a" strokeWidth="2" fill="none"/>
        </>
      )}
      {style === 'surprised' && (
        <>
          <circle cx="7" cy="9" r="6" fill="white" stroke="#333" strokeWidth="1"/>
          <circle cx="7" cy="9" r="3" fill="#1a1a1a"/>
          <circle cx="25" cy="9" r="6" fill="white" stroke="#333" strokeWidth="1"/>
          <circle cx="25" cy="9" r="3" fill="#1a1a1a"/>
        </>
      )}
      {style === 'sleepy' && (
        <>
          <path d="M 2 8 Q 7 12 12 8" stroke="#1a1a1a" strokeWidth="2" fill="none"/>
          <path d="M 20 8 Q 25 12 30 8" stroke="#1a1a1a" strokeWidth="2" fill="none"/>
        </>
      )}
      {style === 'excited' && (
        <>
          <rect x="1" y="2" width="12" height="14" rx="2" fill="white" stroke="#333" strokeWidth="1"/>
          <circle cx="7" cy="10" r="4" fill="#1a1a1a"/>
          <circle cx="5" cy="8" r="1" fill="white"/>
          <rect x="19" y="2" width="12" height="14" rx="2" fill="white" stroke="#333" strokeWidth="1"/>
          <circle cx="25" cy="10" r="4" fill="#1a1a1a"/>
          <circle cx="23" cy="8" r="1" fill="white"/>
        </>
      )}
      {style === 'angry' && (
        <>
          <rect x="2" y="6" width="10" height="8" rx="2" fill="white" stroke="#333" strokeWidth="1"/>
          <circle cx="7" cy="10" r="2" fill="#1a1a1a"/>
          <line x1="0" y1="2" x2="12" y2="6" stroke="#333" strokeWidth="2"/>
          <rect x="20" y="6" width="10" height="8" rx="2" fill="white" stroke="#333" strokeWidth="1"/>
          <circle cx="25" cy="10" r="2" fill="#1a1a1a"/>
          <line x1="32" y1="2" x2="20" y2="6" stroke="#333" strokeWidth="2"/>
        </>
      )}
    </svg>
  </div>
);

// Mouth style preview
const MouthPreview = ({ style }) => (
  <div className="w-10 h-10 flex items-center justify-center bg-cream rounded-lg">
    <svg width="24" height="16" viewBox="0 0 24 16">
      {style === 'smile' && (
        <path d="M 2 4 Q 12 14 22 4" stroke="#D32F2F" strokeWidth="3" fill="none" strokeLinecap="round"/>
      )}
      {style === 'grin' && (
        <>
          <path d="M 2 4 Q 12 16 22 4" stroke="#D32F2F" strokeWidth="2" fill="none"/>
          <rect x="4" y="5" width="16" height="8" rx="2" fill="white"/>
          <line x1="8" y1="5" x2="8" y2="13" stroke="#ccc" strokeWidth="1"/>
          <line x1="12" y1="5" x2="12" y2="13" stroke="#ccc" strokeWidth="1"/>
          <line x1="16" y1="5" x2="16" y2="13" stroke="#ccc" strokeWidth="1"/>
        </>
      )}
      {style === 'neutral' && (
        <line x1="4" y1="8" x2="20" y2="8" stroke="#D32F2F" strokeWidth="3" strokeLinecap="round"/>
      )}
      {style === 'open' && (
        <ellipse cx="12" cy="8" rx="8" ry="6" fill="#D32F2F"/>
      )}
      {style === 'tongue' && (
        <>
          <path d="M 2 4 Q 12 14 22 4" stroke="#D32F2F" strokeWidth="2" fill="none"/>
          <ellipse cx="12" cy="12" rx="5" ry="4" fill="#FF6B6B"/>
        </>
      )}
      {style === 'surprised' && (
        <ellipse cx="12" cy="8" rx="6" ry="7" fill="#1a1a1a"/>
      )}
      {style === 'teeth' && (
        <>
          <rect x="2" y="2" width="20" height="12" rx="3" fill="#D32F2F"/>
          <rect x="4" y="4" width="16" height="5" rx="1" fill="white"/>
          <line x1="8" y1="4" x2="8" y2="9" stroke="#ddd" strokeWidth="1"/>
          <line x1="12" y1="4" x2="12" y2="9" stroke="#ddd" strokeWidth="1"/>
          <line x1="16" y1="4" x2="16" y2="9" stroke="#ddd" strokeWidth="1"/>
        </>
      )}
      {style === 'kiss' && (
        <>
          <circle cx="12" cy="8" r="5" fill="#EC4899"/>
          <ellipse cx="12" cy="7" rx="3" ry="2" fill="#F472B6"/>
        </>
      )}
    </svg>
  </div>
);

// Shoe style preview
const ShoePreview = ({ style }) => (
  <div className="w-10 h-10 flex items-center justify-center bg-cream rounded-lg">
    <svg width="34" height="24" viewBox="0 0 34 24">
      {style === 'trainers' && (
        <>
          <rect x="4" y="8" width="26" height="10" rx="3" fill="#2D3748"/>
          <rect x="4" y="15" width="26" height="4" rx="2" fill="#fff"/>
        </>
      )}
      {style === 'boots' && (
        <>
          <rect x="9" y="2" width="13" height="16" rx="2" fill="#6B4423"/>
          <rect x="7" y="14" width="21" height="6" rx="2" fill="#4A2F16"/>
        </>
      )}
      {style === 'sandals' && (
        <>
          <rect x="4" y="14" width="26" height="5" rx="2" fill="#8B5E3C"/>
          <path d="M 9 14 L 25 6" stroke="#8B5E3C" strokeWidth="3" strokeLinecap="round"/>
          <path d="M 25 14 L 25 6" stroke="#8B5E3C" strokeWidth="3" strokeLinecap="round"/>
        </>
      )}
      {style === 'barefoot' && (
        <rect x="6" y="8" width="22" height="11" rx="5" fill="#E8B98A"/>
      )}
    </svg>
  </div>
);

const PixelCreatorPage = () => {
  const { user, refreshUser } = useAuth();
  const { previewVoice, isPlaying, isLoading: voiceLoading, stop: stopVoice } = useTTS();
  const [activeTab, setActiveTab] = useState('skinTone');
  const [selectedSkinTone, setSelectedSkinTone] = useState(user?.character_skin_tone || '#FFE0BD');
  const [selectedHairColor, setSelectedHairColor] = useState(user?.character_hair_color || '#4a3728');
  const [selectedHairStyle, setSelectedHairStyle] = useState(user?.character_hair_style || 'short');
  const [selectedShirtColor, setSelectedShirtColor] = useState(user?.character_shirt_color || '#4A90D9');
  const [selectedPantsColor, setSelectedPantsColor] = useState(user?.character_pants_color || '#3B82F6');
  const [selectedShoeStyle, setSelectedShoeStyle] = useState(user?.character_shoes || 'trainers');
  const [selectedEyeStyle, setSelectedEyeStyle] = useState(user?.character_eye_style || 'normal');
  const [selectedMouthStyle, setSelectedMouthStyle] = useState(user?.character_mouth_style || 'smile');
  const [selectedAccessory, setSelectedAccessory] = useState(user?.character_accessory || 'none');
  const [selectedVoice, setSelectedVoice] = useState(user?.character_voice || 'alice');
  const [voiceEnabled, setVoiceEnabled] = useState(user?.voice_enabled !== false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const tabs = [
    { id: 'skinTone', label: 'Skin' },
    { id: 'hairColor', label: 'Hair Color' },
    { id: 'hairStyle', label: 'Hair Style' },
    { id: 'shirtColor', label: 'Shirt' },
    { id: 'pantsColor', label: 'Trousers' },
    { id: 'shoeStyle', label: 'Shoes' },
    { id: 'voice', label: 'Voice', icon: Volume2 },
    { id: 'eyeStyle', label: 'Eyes' },
    { id: 'mouthStyle', label: 'Mouth' },
    { id: 'accessory', label: 'Accessory' },
  ];

  const canAfford = (cost) => (user?.credits || 0) >= cost;
  const meetsStage = (requiredStage) => !requiredStage || (user?.stage || 1) >= requiredStage;

  const randomize = () => {
    const getRandomOption = (options) => {
      const affordable = options.filter(o => canAfford(o.cost) && meetsStage(o.stage));
      return affordable[Math.floor(Math.random() * affordable.length)]?.id;
    };
    
    setSelectedSkinTone(getRandomOption(CUSTOMIZATION_OPTIONS.skinTone) || '#FFE0BD');
    setSelectedHairColor(getRandomOption(CUSTOMIZATION_OPTIONS.hairColor) || '#4a3728');
    setSelectedHairStyle(getRandomOption(CUSTOMIZATION_OPTIONS.hairStyle) || 'short');
    setSelectedShirtColor(getRandomOption(CUSTOMIZATION_OPTIONS.shirtColor) || '#4A90D9');
    setSelectedPantsColor(getRandomOption(CUSTOMIZATION_OPTIONS.pantsColor) || '#3B82F6');
    setSelectedShoeStyle(getRandomOption(CUSTOMIZATION_OPTIONS.shoeStyle) || 'trainers');
    setSelectedEyeStyle(getRandomOption(CUSTOMIZATION_OPTIONS.eyeStyle) || 'normal');
    setSelectedMouthStyle(getRandomOption(CUSTOMIZATION_OPTIONS.mouthStyle) || 'smile');
    setSelectedAccessory(getRandomOption(CUSTOMIZATION_OPTIONS.accessory) || 'none');
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    
    try {
      const token = localStorage.getItem('typetown_token');
      await axios.put(`${API}/character/customise`, {
        character_skin_tone: selectedSkinTone,
        character_hair_color: selectedHairColor,
        character_hair_style: selectedHairStyle,
        character_shirt_color: selectedShirtColor,
        character_pants_color: selectedPantsColor,
        character_shoes: selectedShoeStyle,
        character_eye_style: selectedEyeStyle,
        character_mouth_style: selectedMouthStyle,
        character_accessory: selectedAccessory,
        character_voice: selectedVoice,
        voice_enabled: voiceEnabled,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await refreshUser();
      setMessage('Character saved!');
    } catch (err) {
      setMessage('Failed to save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderOptionPreview = (option, tabType) => {
    switch (tabType) {
      case 'skinTone':
      case 'hairColor':
      case 'shirtColor':
      case 'pantsColor':
        return <ColorPreview color={option.id} />;
      case 'hairStyle':
        return <HairPreview style={option.id} color={selectedHairColor} />;
      case 'shoeStyle':
        return <ShoePreview style={option.id} />;
      case 'accessory':
        return <AccessoryPreview type={option.id} />;
      case 'eyeStyle':
        return <EyePreview style={option.id} />;
      case 'mouthStyle':
        return <MouthPreview style={option.id} />;
      case 'voice':
        return (
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
            option.gender === 'female' ? 'bg-pink-100 text-pink-600' : 'bg-blue-100 text-blue-600'
          }`}>
            {option.name[0]}
          </div>
        );
      default:
        return null;
    }
  };

  const renderOptions = () => {
    let options = [];
    let selected = '';
    let setSelected = () => {};
    
    switch (activeTab) {
      case 'skinTone':
        options = CUSTOMIZATION_OPTIONS.skinTone;
        selected = selectedSkinTone;
        setSelected = setSelectedSkinTone;
        break;
      case 'hairColor':
        options = CUSTOMIZATION_OPTIONS.hairColor;
        selected = selectedHairColor;
        setSelected = setSelectedHairColor;
        break;
      case 'hairStyle':
        options = CUSTOMIZATION_OPTIONS.hairStyle;
        selected = selectedHairStyle;
        setSelected = setSelectedHairStyle;
        break;
      case 'shirtColor':
        options = CUSTOMIZATION_OPTIONS.shirtColor;
        selected = selectedShirtColor;
        setSelected = setSelectedShirtColor;
        break;
      case 'pantsColor':
        options = CUSTOMIZATION_OPTIONS.pantsColor;
        selected = selectedPantsColor;
        setSelected = setSelectedPantsColor;
        break;
      case 'shoeStyle':
        options = CUSTOMIZATION_OPTIONS.shoeStyle;
        selected = selectedShoeStyle;
        setSelected = setSelectedShoeStyle;
        break;
      case 'eyeStyle':
        options = CUSTOMIZATION_OPTIONS.eyeStyle;
        selected = selectedEyeStyle;
        setSelected = setSelectedEyeStyle;
        break;
      case 'mouthStyle':
        options = CUSTOMIZATION_OPTIONS.mouthStyle;
        selected = selectedMouthStyle;
        setSelected = setSelectedMouthStyle;
        break;
      case 'accessory':
        options = CUSTOMIZATION_OPTIONS.accessory;
        selected = selectedAccessory;
        setSelected = setSelectedAccessory;
        break;
      case 'voice':
        options = CUSTOMIZATION_OPTIONS.voice;
        selected = selectedVoice;
        setSelected = setSelectedVoice;
        break;
      default:
        break;
    }
    
    // Special rendering for voice tab
    if (activeTab === 'voice') {
      return (
        <div className="space-y-4">
          {/* Voice enabled toggle */}
          <div className="bg-white rounded-2xl p-4 border-2 border-gray-100 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {voiceEnabled ? (
                  <Volume2 className="w-6 h-6 text-mint-green" />
                ) : (
                  <VolumeX className="w-6 h-6 text-text-medium" />
                )}
                <div>
                  <div className="font-bold text-text-dark">Read Aloud</div>
                  <div className="text-sm text-text-medium">Read typing words and NPC messages</div>
                </div>
              </div>
              <button
                onClick={() => setVoiceEnabled(!voiceEnabled)}
                className={`w-14 h-8 rounded-full transition-colors ${
                  voiceEnabled ? 'bg-mint-green' : 'bg-gray-300'
                }`}
              >
                <div className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform ${
                  voiceEnabled ? 'translate-x-7' : 'translate-x-1'
                }`} />
              </button>
            </div>
          </div>
          
          {/* Voice options */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {options.map((option) => {
              const isSelected = selected === option.id;
              const isLocked = !canAfford(option.cost) && !isSelected;
              
              return (
                <button
                  key={option.id}
                  onClick={() => {
                    if (!isLocked) {
                      setSelected(option.id);
                      // Preview the voice
                      previewVoice(option.id, false);
                    }
                  }}
                  disabled={isLocked}
                  className={`
                    relative p-4 rounded-2xl border-2 transition-all text-left
                    ${isSelected ? 'border-mint-green bg-mint-green/10 shadow-md' : 'border-gray-200 hover:border-lavender hover:bg-lavender/5'}
                    ${isLocked ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${
                      option.gender === 'female' 
                        ? 'bg-pink-100 text-pink-600' 
                        : 'bg-blue-100 text-blue-600'
                    }`}>
                      {option.name[0]}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-text-dark">{option.name}</span>
                        {isSelected && <Check className="w-4 h-4 text-mint-green" />}
                      </div>
                      <div className="text-sm text-text-medium">{option.description}</div>
                    </div>
                    {option.cost > 0 && !isSelected && (
                      <div className="flex items-center gap-1 text-sunny-yellow">
                        <Star className="w-4 h-4 fill-sunny-yellow" />
                        <span className="font-bold text-sm">{option.cost}</span>
                      </div>
                    )}
                  </div>
                  {(isPlaying || voiceLoading) && isSelected && (
                    <div className="absolute top-2 right-2">
                      <div className="w-3 h-3 bg-mint-green rounded-full animate-pulse" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          
          {/* Preview button */}
          <button
            onClick={() => previewVoice(selectedVoice, false)}
            disabled={isPlaying || voiceLoading}
            className="w-full py-3 bg-lavender/10 text-lavender font-bold rounded-xl hover:bg-lavender/20 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Volume2 className="w-5 h-5" />
            {isPlaying ? 'Playing...' : voiceLoading ? 'Loading...' : 'Preview Voice'}
          </button>
        </div>
      );
    }
    
    return (
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {options.map((option) => {
          const isSelected = selected === option.id;
          const isLocked = !meetsStage(option.stage) || (!isSelected && option.cost > 0 && !canAfford(option.cost));
          
          return (
            <button
              key={option.id}
              onClick={() => !isLocked && setSelected(option.id)}
              disabled={isLocked}
              className={`
                relative p-3 rounded-2xl border-2 transition-all
                ${isSelected ? 'border-mint-green bg-mint-green/10 scale-105 shadow-md' : 'border-gray-200 hover:border-lavender hover:bg-lavender/5'}
                ${isLocked ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
              `}
              data-testid={`option-${option.id}`}
            >
              <div className="flex justify-center mb-2">
                {renderOptionPreview(option, activeTab)}
              </div>
              <div className="text-xs text-text-dark font-bold text-center">{option.name}</div>
              
              {option.cost > 0 && !isSelected && (
                <div className="mt-1 flex items-center justify-center gap-1">
                  <Star className="w-3 h-3 text-sunny-yellow fill-sunny-yellow" />
                  <span className="text-xs font-bold text-sunny-yellow">{option.cost}</span>
                </div>
              )}
              
              {option.stage && !meetsStage(option.stage) && (
                <div className="absolute top-1 right-1">
                  <Lock className="w-3 h-3 text-text-medium" />
                </div>
              )}
              
              {isSelected && (
                <div className="absolute top-1 right-1">
                  <Check className="w-4 h-4 text-mint-green" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 via-white to-lavender/20">
      {/* Top Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-text-medium hover:text-mint-green transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-bold">Back</span>
          </Link>
          
          <div className="font-display text-xl font-bold">
            <span className="text-lavender">Character</span>
            <span className="text-coral-orange ml-2">Studio</span>
            <Sparkles className="w-5 h-5 text-sunny-yellow inline ml-2" />
          </div>
          
          <div className="flex items-center gap-2 bg-sunny-yellow/20 rounded-full px-4 py-2 border-2 border-sunny-yellow">
            <Star className="w-4 h-4 text-sunny-yellow fill-sunny-yellow" />
            <span className="font-bold text-text-dark" data-testid="credits-display">
              {user?.credits || 0}
            </span>
          </div>
        </div>
      </nav>
      
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Preview */}
            <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-gray-100 flex flex-col items-center" data-testid="character-preview-panel">
              <div className="flex items-center justify-between w-full mb-4">
                <h2 className="text-xl font-bold text-text-dark">Your Character</h2>
                <button
                  onClick={randomize}
                  className="flex items-center gap-2 px-4 py-2 bg-lavender/20 hover:bg-lavender/30 rounded-2xl text-lavender font-bold text-sm transition-colors"
                  data-testid="randomize-btn"
                >
                  <RotateCcw className="w-4 h-4" />
                  Surprise Me!
                </button>
              </div>
              
              <div className="relative my-4 p-8 bg-gradient-to-b from-sky-100 to-mint-green/20 rounded-2xl">
                <BlockyCharacter3D 
                  size={180} 
                  skinTone={selectedSkinTone}
                  hairColor={selectedHairColor}
                  hairStyle={selectedHairStyle}
                  shirtColor={selectedShirtColor}
                  pantsColor={selectedPantsColor}
                  shoes={selectedShoeStyle}
                  eyeStyle={selectedEyeStyle}
                  mouthStyle={selectedMouthStyle}
                  accessory={selectedAccessory}
                  direction="down"
                  isMoving={false}
                />
              </div>
              
              <div className="text-sm text-text-medium mb-4">
                Level {user?.stage || 1} · {['Newcomer', 'Explorer', 'Builder', 'Master', 'Champion'][(user?.stage || 1) - 1]}
              </div>
              
              {message && (
                <div className={`mb-4 px-4 py-2 rounded-2xl text-sm font-bold ${
                  message.includes('saved') ? 'bg-mint-green/20 text-mint-green' : 'bg-coral-orange/20 text-coral-orange'
                }`}>
                  {message}
                </div>
              )}
              
              <button
                onClick={handleSave}
                disabled={saving}
                className="font-bold bg-gradient-to-r from-mint-green to-sky-blue text-white px-8 py-3 rounded-2xl hover:scale-105 transition-all disabled:opacity-50 shadow-lg"
                data-testid="save-character-btn"
              >
                {saving ? 'Saving...' : 'Save My Look!'}
              </button>
            </div>
            
            {/* Customization */}
            <div className="bg-white rounded-3xl p-4 sm:p-6 shadow-lg border-2 border-gray-100" data-testid="customization-panel">
              {/* Tabs */}
              <div className="flex gap-2 mb-5 overflow-x-auto pb-2 scrollbar-hide">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      px-4 py-2 rounded-2xl text-sm font-bold whitespace-nowrap transition-all
                      ${activeTab === tab.id 
                        ? 'bg-lavender text-white scale-105 shadow-md' 
                        : 'bg-gray-100 text-text-medium hover:text-text-dark hover:bg-gray-200'
                      }
                    `}
                    data-testid={`tab-${tab.id}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              
              {/* Options */}
              <div className="max-h-[400px] overflow-y-auto pr-2">
                {renderOptions()}
              </div>
              
              {/* Info */}
              <div className="mt-5 p-4 bg-sunny-yellow/10 rounded-2xl border-2 border-sunny-yellow/30">
                <p className="text-sm text-text-medium">
                  <Star className="w-4 h-4 text-sunny-yellow fill-sunny-yellow inline mr-1" />
                  Type well to earn coins! Unlock cool stuff as you level up!
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PixelCreatorPage;
